/**
 * Unified AI Assistant Component
 * 
 * Renders the AI assistant panel when not on dashboard.
 * On dashboard, the AIAssistantCard renders within the page layout.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Shield } from 'lucide-react';
import { AIAssistantChat } from './AIAssistantChat';
import { useAIAssistantUI } from '../../context/AIAssistantUIContext';
import { useIncidentAssistantSafe } from '../../context/IncidentAssistantContext';
import { cn } from '../../lib/utils';

export function AIAssistantUnified() {
  const { isOpen, togglePanel, closePanel, isDashboard } = useAIAssistantUI();
  const assistantContext = useIncidentAssistantSafe();
  const hasIncidentContext = !isDashboard && assistantContext?.incidentData;

  // On dashboard, don't render - the AIAssistantCard in Dashboard handles it
  if (isDashboard) {
    return null;
  }

  // Sidebar mode: render as slide-out panel
  return (
    <>
      {/* Toggle button */}
      <motion.button
        onClick={togglePanel}
        className={cn(
          'fixed right-0 top-1/2 -translate-y-1/2 z-50',
          'p-2 bg-brand-blue text-white rounded-l-lg shadow-lg',
          'hover:bg-blue-hover transition-all duration-200',
          'flex flex-col items-center gap-1',
          isOpen && 'opacity-0 pointer-events-none'
        )}
        title="AI Assistant (⌘K)"
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 0 : 1 }}
        transition={{ duration: 0.2 }}
      >
        <Bot className="w-5 h-5" />
        {hasIncidentContext && <Shield className="w-3 h-3 text-blue-200" />}
      </motion.button>

      {/* Backdrop - click to close */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-30 bg-black/10 dark:bg-black/30 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              closePanel();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-label="Close assistant panel"
          />
        )}
      </AnimatePresence>

      {/* Panel */}
      <motion.div
        className={cn(
          'fixed right-0 top-0 bottom-0 z-40 w-screen max-w-96',
          'bg-bg-primary shadow-2xl',
          'border-l border-border-default',
          !isOpen && 'pointer-events-none'
        )}
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? 0 : '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <AIAssistantChat mode="panel" />
      </motion.div>
    </>
  );
}
